import { DateOnly } from '@if/core/date'
import { NewLoanDtoSchemaType } from '@src/components/LoanForm/loan-form-schema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { apiClient } from '@src/services/http-common'
import {
  CalendarSimulation,
  ComputeCalendarSimulationAction,
  SignedCalendarSimulation,
  SpreadOverdueAmount,
} from '@src/types/loan/loan-simulation'
import { QueryObserverBaseResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LoanLockDto, SignedCalendarSimulationDto, SpreadOverdueAmountResultDto } from './loan-dto'
import {
  transformComputeCalendarActionToDto as transformComputeCalendarSimulationActionToDto,
  transformSignedCalendarSimulationFromDto,
  transformSignedCalendarSimulationToDto,
  transformSpreadOverdueAmountToDto,
} from './loan-transformer'

const SCOPE = 'loans'

// QueryKey factory for consistent query key generation
export const loanQueryKeys = {
  all: [SCOPE] as const,
  loans: () => [...loanQueryKeys.all, 'loan'] as const,
  loan: (id: string) => [...loanQueryKeys.loans(), id] as const,
  loanCalendar: (id: string, financingProgramId: string) =>
    [...loanQueryKeys.loan(id), financingProgramId, 'loanCalendar'] as const,
  computeAction: (id: string) => [...loanQueryKeys.loan(id), 'computeAction'] as const,
  applySimulation: (id: string) => [...loanQueryKeys.loan(id), 'applySimulation'] as const,
  refreshSimulation: (id: string) => [...loanQueryKeys.loan(id), 'refreshSimulation'] as const,
  computeCatchUpAmountOfOverdueForDays: (
    simulation: CalendarSimulation,
    startDate: DateOnly,
    endDate?: DateOnly | null,
  ) =>
    [
      ...loanQueryKeys.loan(simulation.fullLoan.loan.id),
      'computeCatchUpAmountOfOverdueForDays',
      simulation,
      startDate,
      endDate,
    ] as const,
  calculateAmountToSpreadOver: (
    id: string,
    simulation: SignedCalendarSimulation,
    numberOfAffectedPayment: number,
    fromPaymentDate: DateOnly,
  ) =>
    [
      ...loanQueryKeys.loan(id),
      'calculateAmountToSpreadOver',
      simulation,
      numberOfAffectedPayment,
      fromPaymentDate,
    ] as const,
  calculateNumberOfPaymentsToClearOverdueAmount: (
    id: string,
    financingProgramId: string,
    simulation: SignedCalendarSimulation,
    fromPaymentDate: DateOnly,
    amountPerPayment: number,
  ) =>
    [
      ...loanQueryKeys.loan(id),
      'calculateNumberOfPaymentsToClearOverdueAmount',
      financingProgramId,
      simulation,
      fromPaymentDate,
      amountPerPayment,
    ] as const,
  loanLock: (id: string, financingProgramId: string) =>
    [...loanQueryKeys.loan(id), financingProgramId, 'lock'] as const,
  acquireLock: (id: string) => [...loanQueryKeys.loan(id), 'acquireLock'] as const,
  releaseLock: (id: string) => [...loanQueryKeys.loan(id), 'releaseLock'] as const,
  replayMigrationEvents: (id: string) => [...loanQueryKeys.loan(id), 'replayMigrationEvents'] as const,
}

export function postCreateLoan(newLoan: NewLoanDtoSchemaType) {
  return apiClient.post('Loans', newLoan)
}

const getCalendarSimulation = async (id: string, financingProgramId: string): Promise<SignedCalendarSimulation> => {
  const response = await apiClient.get<SignedCalendarSimulationDto>(
    `Loans/${financingProgramId}/${id}/CalendarSimulation`,
  )
  return transformSignedCalendarSimulationFromDto(response.data)
}

export function useGetCalendarSimulation(
  id: string,
  financingProgramId: string,
): [
  SignedCalendarSimulation | undefined,
  boolean,
  Error | null,
  QueryObserverBaseResult<SignedCalendarSimulation | undefined>['refetch'],
] {
  const { isFetching, data, error, refetch } = useQuery({
    queryKey: loanQueryKeys.loanCalendar(id, financingProgramId),
    queryFn: () => getCalendarSimulation(id, financingProgramId),
    enabled: !!id,
  })

  return [data, isFetching, error, refetch]
}

const computeCalendarAction = async (
  id: string,
  financingProgramId: string,
  action: ComputeCalendarSimulationAction,
): Promise<SignedCalendarSimulation> => {
  const actionDto = transformComputeCalendarSimulationActionToDto(action)
  const response = await apiClient.post<SignedCalendarSimulationDto>(
    `Loans/${financingProgramId}/${id}/CalendarSimulation/ComputeAction`,
    actionDto,
  )
  return transformSignedCalendarSimulationFromDto(response.data)
}

export function useComputeCalendarAction(id: string, financingProgramId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: loanQueryKeys.computeAction(id),
    mutationFn: (action: ComputeCalendarSimulationAction) => computeCalendarAction(id, financingProgramId, action),
    onSuccess: async (simulation) => {
      // Automatically acquire lock if simulation has an expiration date
      if (simulation.simulation.simulationExpiresOn) {
        try {
          const lockResult = await acquireLock(id, financingProgramId)
          // Update lock state in cache
          queryClient.setQueryData(loanQueryKeys.loanLock(id, financingProgramId), lockResult)
        } catch (error) {
          // Lock acquisition failure is not critical - user can continue with simulation
          console.warn('Failed to automatically acquire lock:', error)
        }
      }
    },
  })
}

const applyCalendarSimulation = async (
  id: string,
  financingProgramId: string,
  simulation: SignedCalendarSimulation,
): Promise<SignedCalendarSimulation> => {
  const simulationDto = transformSignedCalendarSimulationToDto(simulation)
  const response = await apiClient.post<SignedCalendarSimulationDto>(
    `Loans/${financingProgramId}/${id}/CalendarSimulation/ApplySimulation`,
    simulationDto,
  )
  return transformSignedCalendarSimulationFromDto(response.data)
}

export function useApplyCalendarSimulation(id: string, financingProgramId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: loanQueryKeys.applySimulation(id),
    mutationFn: (simulation: SignedCalendarSimulation) => applyCalendarSimulation(id, financingProgramId, simulation),
    onSuccess: async (data) => {
      queryClient.setQueryData(loanQueryKeys.loanCalendar(id, financingProgramId), data)

      // Automatically release lock after successful save
      try {
        const lockResult = await releaseLock(id, financingProgramId)
        // Update lock state in cache
        queryClient.setQueryData(loanQueryKeys.loanLock(id, financingProgramId), lockResult)
      } catch (error) {
        // Lock release failure is not critical - user can manually release if needed
        console.warn('Failed to automatically release lock:', error)
      }
    },
  })
}

const calculateAmountToSpread = async (
  id: string,
  financingProgramId: string,
  spreadOverdueAmount: SpreadOverdueAmount,
): Promise<SpreadOverdueAmountResultDto> => {
  const dto = transformSpreadOverdueAmountToDto(spreadOverdueAmount)
  const response = await apiClient.post<SpreadOverdueAmountResultDto>(
    `Loans/${financingProgramId}/${id}/CalendarSimulation/CalculateAmountToSpreadOver`,
    dto,
  )
  return response.data
}

export function useCalculateAmountToSpread(id: string, financingProgramId: string) {
  return useMutation({
    mutationKey: loanQueryKeys.computeAction(id),
    mutationFn: (spreadOverdueAmount: SpreadOverdueAmount) =>
      calculateAmountToSpread(id, financingProgramId, spreadOverdueAmount),
  })
}

const refreshSimulation = async (
  id: string,
  financingProgramId: string,
  simulation: SignedCalendarSimulation,
): Promise<SignedCalendarSimulation> => {
  const simulationDto = transformSignedCalendarSimulationToDto(simulation)
  const response = await apiClient.post<SignedCalendarSimulationDto>(
    `Loans/${financingProgramId}/${id}/CalendarSimulation/RefreshSimulation`,
    simulationDto,
  )
  return transformSignedCalendarSimulationFromDto(response.data)
}

export function useRefreshSimulation(id: string, financingProgramId: string) {
  return useMutation({
    mutationKey: loanQueryKeys.refreshSimulation(id),
    mutationFn: (simulation: SignedCalendarSimulation) => refreshSimulation(id, financingProgramId, simulation),
  })
}

const computeCatchUpAmountOfOverdueForDays = async (
  simulation: CalendarSimulation,
  startDate: DateOnly,
  endDate?: DateOnly | null,
): Promise<Map<string, number>> => {
  const simulationDto = CalendarSimulation.toDto(simulation)
  const response = await apiClient.post<{ date: string; amount: number }[]>(
    `Loans/${simulation.fullLoan.loan.financingProgramId}/${simulation.fullLoan.loan.id}/CalendarSimulation/ComputeCatchUpAmountOfOverdueForDays`,
    {
      simulation: simulationDto,
      startDate,
      endDate: endDate ?? null,
    },
  )
  return new Map(response.data.map((item) => [item.date, item.amount]))
}

export function useComputeCatchUpAmountOfOverdueForDays(
  simulation: CalendarSimulation,
  startDate: DateOnly,
  endDate?: DateOnly | null,
  enabled = true,
) {
  return useQuery<Map<string, number>>({
    queryKey: loanQueryKeys.computeCatchUpAmountOfOverdueForDays(simulation, startDate, endDate),
    queryFn: () => computeCatchUpAmountOfOverdueForDays(simulation, startDate, endDate),
    enabled,
  })
}

export interface CalculateNumberOfPaymentsToClearOverdueAmountResult {
  numberOfPayments: number
  lastPaymentAmount: number
}

const calculateNumberOfPaymentsToClearOverdueAmount = async (
  id: string,
  financingProgramId: string,
  simulation: SignedCalendarSimulation,
  fromPaymentDate: DateOnly,
  amountPerPayment: number,
): Promise<CalculateNumberOfPaymentsToClearOverdueAmountResult> => {
  const simulationDto = transformSignedCalendarSimulationToDto(simulation)
  const dto = {
    simulation: simulationDto.simulation,
    fromPaymentDate: fromPaymentDate.toISOString(),
    amountPerPayment,
  }
  const response = await apiClient.post<{ numberOfPayments: number; lastPaymentAmount: number }>(
    `Loans/${financingProgramId}/${id}/CalendarSimulation/CalculateNumberOfPaymentsToClearOverdueAmount`,
    dto,
  )
  return {
    numberOfPayments: response.data.numberOfPayments,
    lastPaymentAmount: response.data.lastPaymentAmount,
  }
}

export function useCalculateNumberOfPaymentsToClearOverdueAmount(
  id: string,
  financingProgramId: string,
  simulation: SignedCalendarSimulation,
  fromPaymentDate: DateOnly,
  amountPerPayment: number,
  enabled = true,
) {
  return useQuery<CalculateNumberOfPaymentsToClearOverdueAmountResult>({
    queryKey: loanQueryKeys.calculateNumberOfPaymentsToClearOverdueAmount(
      id,
      financingProgramId,
      simulation,
      fromPaymentDate,
      amountPerPayment,
    ),
    queryFn: () =>
      calculateNumberOfPaymentsToClearOverdueAmount(
        id,
        financingProgramId,
        simulation,
        fromPaymentDate,
        amountPerPayment,
      ),
    enabled: enabled && !!id && !!fromPaymentDate && amountPerPayment > 0,
  })
}

const acquireLock = async (loanId: string, financingProgramId: string): Promise<LoanLockDto> => {
  const response = await apiClient.post<LoanLockDto>(`Loans/${financingProgramId}/${loanId}/Reservation`)
  return response.data
}

export function useAcquireLock(loanId: string, financingProgramId: string) {
  return useMutation({
    mutationKey: loanQueryKeys.acquireLock(loanId),
    mutationFn: () => acquireLock(loanId, financingProgramId),
  })
}

const releaseLock = async (loanId: string, financingProgramId: string): Promise<LoanLockDto> => {
  const response = await apiClient.delete<LoanLockDto>(`Loans/${financingProgramId}/${loanId}/Reservation`)
  return response.data
}

export function useReleaseLock(loanId: string, financingProgramId: string) {
  return useMutation({
    mutationKey: loanQueryKeys.releaseLock(loanId),
    mutationFn: () => releaseLock(loanId, financingProgramId),
  })
}

const getLock = async (loanId: string, financingProgramId: string): Promise<LoanLockDto> => {
  const response = await apiClient.get<LoanLockDto>(`Loans/${financingProgramId}/${loanId}/Reservation`)
  return response.data
}

export function useGetLock(loanId: string, financingProgramId: string, enabled = true) {
  return useQuery({
    queryKey: loanQueryKeys.loanLock(loanId, financingProgramId),
    queryFn: () => getLock(loanId, financingProgramId),
    enabled: enabled && !!loanId && !!financingProgramId,
  })
}

const replayMigrationEvents = async (loanId: string): Promise<void> => {
  try {
    await apiClient.post(`Loans/${loanId}/replay-migration-events`)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

export function useReplayMigrationEvents(loanId: string) {
  return useMutation({
    mutationKey: loanQueryKeys.replayMigrationEvents(loanId),
    mutationFn: () => replayMigrationEvents(loanId),
  })
}
